import React, { useState, useEffect } from "react";
import { Modal, Box, TextField, Button, IconButton, Typography, CircularProgress, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { postFiles, put } from "../../config/axios";

const EditVideoModal = ({ open, onClose, videoData, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: videoData?.title || "",
    description: videoData?.description || "",
    benefits: videoData?.benefits?.join(", ") || "",
    caloriesBurn: videoData?.caloriesBurn !== undefined && videoData?.caloriesBurn !== null ? String(videoData.caloriesBurn) : "",
    thumbnailFile: null,
    videoFile: null,
    coverImageFile: null,
  });

  useEffect(() => {
    setFormData({
      title: videoData?.title || "",
      description: videoData?.description || "",
      benefits: videoData?.benefits?.join(", ") || "",
      caloriesBurn: videoData?.caloriesBurn !== undefined && videoData?.caloriesBurn !== null ? String(videoData.caloriesBurn) : "",
      thumbnailFile: null,
      videoFile: null,
      coverImageFile: null,
    });
  }, [videoData]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [fileInfo, setFileInfo] = useState({});

  // File validation constants
  const FILE_LIMITS = {
    thumbnail: { maxSize: 5 * 1024 * 1024, formats: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'] }, // 5MB
    video: { maxSize: 100 * 1024 * 1024, formats: ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv'] }, // 100MB
    coverImage: { maxSize: 5 * 1024 * 1024, formats: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'] } // 5MB
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const handleCaloriesBurnChange = (e) => {
    const value = e.target.value;
    // Only allow numeric characters
    if (value === '' || /^\d+$/.test(value)) {
      setFormData({ ...formData, caloriesBurn: value });
      if (errors.caloriesBurn) {
        setErrors({ ...errors, caloriesBurn: "" });
      }
    }
  };

  const validateFile = (file, fileType) => {
    const limits = FILE_LIMITS[fileType];

    if (!file) return { isValid: false, message: `${fileType} is required` };

    // Check file size
    if (file.size > limits.maxSize) {
      const maxSizeMB = limits.maxSize / (1024 * 1024);
      return {
        isValid: false,
        message: `${fileType} size must be less than ${maxSizeMB}MB`
      };
    }

    // Check file format
    if (!limits.formats.includes(file.type)) {
      const allowedFormats = limits.formats.map(f => f.split('/')[1].toUpperCase()).join(', ');
      return {
        isValid: false,
        message: `${fileType} must be in ${allowedFormats} format`
      };
    }

    return { isValid: true, message: "" };
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const fieldName = e.target.name;

    if (file) {
      const fileType = fieldName === 'thumbnailFile' ? 'thumbnail' :
        fieldName === 'videoFile' ? 'video' : 'coverImage';

      const validation = validateFile(file, fileType);

      if (validation.isValid) {
        setFormData({ ...formData, [fieldName]: file });
        setFileInfo({ ...fileInfo, [fieldName]: { name: file.name, size: file.size } });
        // Clear error when user selects a valid file
        if (errors[fieldName]) {
          setErrors({ ...errors, [fieldName]: "" });
        }
      } else {
        setErrors({ ...errors, [fieldName]: validation.message });
        setFormData({ ...formData, [fieldName]: null });
        setFileInfo({ ...fileInfo, [fieldName]: null });
      }
    } else {
      setFormData({ ...formData, [fieldName]: null });
      setFileInfo({ ...fileInfo, [fieldName]: null });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.benefits.trim()) {
      newErrors.benefits = "Benefits are required";
    }

    if (
      formData.caloriesBurn === undefined ||
      formData.caloriesBurn === null ||
      (typeof formData.caloriesBurn === "string" && !formData.caloriesBurn.trim()) ||
      (typeof formData.caloriesBurn === "number" && isNaN(formData.caloriesBurn))
    ) {
      newErrors.caloriesBurn = "Calories Burn is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = { ...formData };
      payload.benefits = formData.benefits.split(",").map((item) => item.trim());

      // Convert caloriesBurn to number
      if (formData.caloriesBurn) {
        payload.caloriesBurn = parseInt(formData.caloriesBurn, 10);
      }

      if (formData.thumbnailFile) {
        const form = new FormData();
        form.append("file", formData.thumbnailFile);
        const thumbnailResponse = await postFiles("/api/app/user/uploadImage", form);
        payload.thumbnail = thumbnailResponse.data.url;
      }

      if (formData.videoFile) {
        const form = new FormData();
        form.append("file", formData.videoFile);
        const videoResponse = await postFiles("/api/app/user/uploadImage", form);
        payload.videoUrl = videoResponse.data.url;
      }

      if (formData.coverImageFile) {
        const form = new FormData();
        form.append("file", formData.coverImageFile);
        const coverImageResponse = await postFiles("/api/app/user/uploadImage", form);
        payload.coverImage = coverImageResponse.data.url;
      }

      // Remove file fields from payload as they're not part of the model
      delete payload.thumbnailFile;
      delete payload.videoFile;
      delete payload.coverImageFile;

      console.log('Sending payload:', payload);
      const res = await put(`/api/dashboard/yogaFeatures/updateYogaVideo/${videoData?._id}`, payload);
      console.log('::::::::check the res:::::::', res);

      if (res && res.message) {
        // Show success message
        console.log('Update successful:', res.message);
      }

      onSuccess();
    } catch (error) {
      console.error("Error updating video:", error);

      // Show more detailed error information
      if (error.response) {
        console.error("Error response:", error.response.data);
        if (error.response.data && error.response.data.errors) {
          const validationErrors = error.response.data.errors;
          const newErrors = {};
          validationErrors.forEach(err => {
            newErrors[err.path] = err.msg;
          });
          setErrors(newErrors);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          width: "800px",
          position: "relative",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <IconButton
          style={{ position: "absolute", top: 8, right: 8 }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h6" mb={2}>
          Edit Video
        </Typography>
        <TextField
          fullWidth
          label={
            <span>
              Title <span style={{ color: "red" }}>*</span>
            </span>
          }
          name="title"
          value={formData.title}
          onChange={handleChange}
          margin="normal"
          error={!!errors.title}
          helperText={errors.title}
        />
        <TextField
          fullWidth
          label={
            <span>
              Description <span style={{ color: "red" }}>*</span>
            </span>
          }
          name="description"
          value={formData.description}
          onChange={handleChange}
          margin="normal"
          multiline
          rows={3}
          error={!!errors.description}
          helperText={errors.description}
        />
        <TextField
          fullWidth
          label={
            <span>
              Benefits (comma-separated) <span style={{ color: "red" }}>*</span>
            </span>
          }
          name="benefits"
          value={formData.benefits}
          onChange={handleChange}
          margin="normal"
          error={!!errors.benefits}
          helperText={errors.benefits}
        />
        <TextField
          fullWidth
          label={
            <span>
              Calories Burn <span style={{ color: "red" }}>*</span>
            </span>
          }
          name="caloriesBurn"
          value={formData.caloriesBurn}
          onChange={handleCaloriesBurnChange}
          margin="normal"
          error={!!errors.caloriesBurn}
          helperText={errors.caloriesBurn}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*'
          }}
        />
        {/* Thumbnail Preview */}
        {videoData?.thumbnail && (
          <img src={videoData.thumbnail} alt="Current Thumbnail" style={{ maxWidth: 120, maxHeight: 80, marginBottom: 8 }} />
        )}
        <Typography variant="subtitle1" mt={2} mb={-1}>
          Upload Thumbnail
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem', color: '#666' }}>
          Supported formats: JPG, JPEG, PNG, WEBP | Max size: 5MB
        </Typography>
        <TextField
          fullWidth
          type="file"
          name="thumbnailFile"
          onChange={handleFileChange}
          margin="normal"
          error={!!errors.thumbnailFile}
          helperText={errors.thumbnailFile || (fileInfo.thumbnailFile && `Selected: ${fileInfo.thumbnailFile.name} (${formatFileSize(fileInfo.thumbnailFile.size)})`)}
          inputProps={{
            accept: "image/jpeg,image/jpg,image/png,image/webp"
          }}
        />
        {/* Video Preview */}
        {videoData?.videoUrl && (
          <video src={videoData.videoUrl} controls style={{ maxWidth: 200, marginBottom: 8 }} />
        )}
        <Typography variant="subtitle1" mt={2} mb={-1}>
          Upload Video
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem', color: '#666' }}>
          Supported formats: MP4, AVI, MOV, WMV, FLV | Max size: 100MB
        </Typography>
        <TextField
          fullWidth
          type="file"
          name="videoFile"
          onChange={handleFileChange}
          margin="normal"
          error={!!errors.videoFile}
          helperText={errors.videoFile || (fileInfo.videoFile && `Selected: ${fileInfo.videoFile.name} (${formatFileSize(fileInfo.videoFile.size)})`)}
          inputProps={{
            accept: "video/mp4,video/avi,video/mov,video/wmv,video/flv"
          }}
        />
        {/* Cover Image Preview */}
        {videoData?.coverImage && (
          <img src={videoData.coverImage} alt="Current Cover" style={{ maxWidth: 120, maxHeight: 80, marginBottom: 8 }} />
        )}
        <Typography variant="subtitle1" mt={2} mb={-1}>
          Upload Cover Image
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem', color: '#666' }}>
          Supported formats: JPG, JPEG, PNG, WEBP | Max size: 5MB
        </Typography>
        <TextField
          fullWidth
          type="file"
          name="coverImageFile"
          onChange={handleFileChange}
          margin="normal"
          error={!!errors.coverImageFile}
          helperText={errors.coverImageFile || (fileInfo.coverImageFile && `Selected: ${fileInfo.coverImageFile.name} (${formatFileSize(fileInfo.coverImageFile.size)})`)}
          inputProps={{
            accept: "image/jpeg,image/jpg,image/png,image/webp"
          }}
        />
        <Button
          fullWidth
          variant="contained"
          sx={{ mt: 2 }}
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? "Updating..." : "Update"}
        </Button>
      </Box>
    </Modal>
  );
};

export default EditVideoModal;
